import { env } from "@/lib/env";

/**
 * First-party home-page analytics (no third-party trackers). Events are batched and sent to
 * POST /api/v1/events/; on page hide the rest go out with sendBeacon.
 */
export type EventName =
  | "home_view"
  | "home_search_used"
  | "home_category_tap"
  | "home_listing_tap"
  | "home_sell_tap"
  | "realitysafe_sheet_open"
  | "home_scroll_depth"
  | "home_bounce"
  | "clarity_survey";

type Props = Record<string, string | number | boolean>;
interface Queued {
  name: EventName;
  path: string;
  props?: Props;
}

const queue: Queued[] = [];
let timer = 0;
let engaged = false;
const ENGAGED: EventName[] = ["home_search_used", "home_category_tap", "home_listing_tap", "home_sell_tap", "realitysafe_sheet_open"];

/** One id per visit (tab session), so "sessions" and bounces mean visits, not browsers. */
export function visitId(): string {
  try {
    let id = sessionStorage.getItem("rs:visit");
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem("rs:visit", id);
    }
    return id;
  } catch {
    return "";
  }
}

function payload(events: Queued[]): string {
  return JSON.stringify({ session: visitId(), events });
}

function flush(beacon = false) {
  window.clearTimeout(timer);
  if (!queue.length) return;
  const batch = queue.splice(0, 20);
  const url = `${env.apiBaseUrl}/events/`;
  const body = payload(batch);
  try {
    if (beacon && navigator.sendBeacon) navigator.sendBeacon(url, new Blob([body], { type: "text/plain" }));
    else void fetch(url, { method: "POST", body, headers: { "Content-Type": "application/json" }, keepalive: true }).catch(() => undefined);
  } catch {
    /* analytics never breaks the page */
  }
  if (queue.length) flush(beacon);
}

export function track(name: EventName, props?: Props) {
  if (typeof window === "undefined" || import.meta.env.MODE === "test") return;
  if (ENGAGED.includes(name)) engaged = true;
  queue.push({ name, path: window.location.pathname, props });
  window.clearTimeout(timer);
  timer = window.setTimeout(() => flush(), 4000);
}

export function hasEngaged(): boolean {
  return engaged;
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush(true));
}
